import { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import "./BarcodePrinterPopup.css";
import { electronService } from "@/services/electronService";

interface BarcodePrinterPopupProps {
  onSelect: (result: { printerName: string; copies: number } | null) => void;
  printers: any[];
  defaultPrinterName: string;
  defaultQuantity?: number;
}

const BarcodePrinterPopup = ({ onSelect, printers, defaultPrinterName, defaultQuantity }: BarcodePrinterPopupProps) => {
  const [selectedPrinter, setSelectedPrinter] = useState<string>(defaultPrinterName);
  const [copies, setCopies] = useState<number | ''>(defaultQuantity || 1);

  const handlePrint = async () => {
    if (selectedPrinter) {
      await electronService.saveSelectedBarcodePrinter(selectedPrinter);
    }
    const finalCopies = typeof copies === 'number' && copies > 0 ? copies : 1;
    onSelect({ printerName: selectedPrinter, copies: finalCopies });
  };

  return (
    <div className="printerModalOverlay">
      <div className="printerModal">
        <div className="printerModalHeader">
          <h3>Print Barcode Labels</h3>
          <button className="printerModalClose" onClick={() => onSelect(null)}>
            <X />
          </button>
        </div>

        <div className="printerModalContent">
          {printers.length > 0 ? (
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold", color: "var(--app-modal-text)" }}>
                Printer
              </label>
              <select
                value={selectedPrinter}
                onChange={(e) => setSelectedPrinter(e.target.value)}
                style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
              >
                <option value="">Select a printer...</option>
                {printers.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <p style={{ margin: 0, color: "#666" }}>Loading printers or no printers found...</p>
          )}

          {defaultQuantity !== undefined && (
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold", color: "var(--app-modal-text)" }}>
                Number of Barcodes
              </label>
              <input
                type="number"
                min="1"
                value={copies}
                onChange={(e) => {
                  if (e.target.value === '') {
                    setCopies('');
                  } else {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) setCopies(val);
                  }
                }}
                style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
              />
            </div>
          )}
        <div className="printerModalActions">
          <Button variant="outline" onClick={() => onSelect(null)} style={{ borderRadius: 50, padding: "12px 24px" }}>
            Cancel
          </Button>
          <Button onClick={handlePrint} style={{ borderRadius: 50, padding: "12px 24px" }} disabled={!selectedPrinter || copies === '' || copies < 1}>
            Print
          </Button>
        </div>
        </div>
      </div>
    </div>
  );
};

export const selectPrinter = async (defaultQuantity?: number): Promise<{ printerName: string; copies: number } | null> => {
  if (electronService.isElectron()) {
    try {
      const [printers, settings] = await Promise.all([
        electronService.getPrinters(),
        electronService.getPrinterSettings(),
      ]);

      let validatedDefaultName = settings?.effective || settings?.default || "";

      if (settings?.barcodeSelected) {
        // Validation: verify if the saved printer actually still exists
        const exists = printers.some(p => p.name === settings.barcodeSelected);
        if (exists) {
          validatedDefaultName = settings.barcodeSelected;
        } else {
          // It's saved but no longer in the system. Clear it.
          await electronService.saveSelectedBarcodePrinter("");
        }
      }

      // Always show the modal
      return new Promise((resolve) => {
        const div = document.createElement("div");
        document.body.appendChild(div);
        const root = createRoot(div);

        const cleanup = () => {
          setTimeout(() => {
            root.unmount();
            div.remove();
          }, 0);
        };

        const handleSelect = (result: { printerName: string; copies: number } | null) => {
          resolve(result);
          cleanup();
        };

        root.render(<BarcodePrinterPopup onSelect={handleSelect} printers={printers} defaultPrinterName={validatedDefaultName} defaultQuantity={defaultQuantity} />);
      });
    } catch (e) {
      console.error("Failed to load printers:", e);
      return null;
    }
  }

  return null;
};
